import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  InfluencerDetail,
  PasswordResetResponse,
  PersonControllerService,
  PersonDetail,
  SubscriberControllerService
} from 'src/app/admin-api';
import { SubscriptionEnum, SubscriptionId } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { PWAUtil } from 'src/app/utils/pwa.util';
import { ChangeCouponModalComponent } from '../change-coupon-modal/change-coupon-modal.component';

@Component({
  selector: 'app-person-card',
  templateUrl: './person-card.component.html',
  styleUrls: ['./person-card.component.scss'],
  providers: [DialogService, MessageService, ConfirmationService],
  encapsulation: ViewEncapsulation.None
})
export class PersonCardComponent {
  private dialogService = inject(DialogService);
  private cdRef = inject(ChangeDetectorRef);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private personService = inject(PersonControllerService);
  private router = inject(Router);
  private subscriberService = inject(SubscriberControllerService);
  private clipboard = inject(Clipboard);

  @Input()
  person: PersonDetail | undefined;

  @Input()
  subscriptionId = 1;

  @Output()
  couponChanged = new EventEmitter<string>();

  @Input()
  isMobile?: boolean;

  @Input()
  influencer: InfluencerDetail | undefined;

  @Input()
  showProfileLink = true;

  @Input()
  showResetPassword = true;

  passwordResult: PasswordResetResponse | undefined;

  changeCouponName(): void {
    this.dialogService
      .open(ChangeCouponModalComponent, {
        closable: true,
        header: `Você quer alterar o cupom ${this.person?.couponName}?`,
        position: 'center',
        closeOnEscape: false,
        data: {
          person: this.person
        },
        width: this.isMobile ? '100%' : '50%'
      })
      .onClose.subscribe((coupon: string) => {
        if (this.person && coupon) {
          this.person.couponName = coupon;
          this.cdRef.detectChanges();
          this.couponChanged.emit(coupon);
          this.messageService.add({
            detail: 'Cupom alterado com sucesso',
            summary: 'Sucesso',
            severity: 'success'
          });
        }
      });
  }

  resetPassword(): void {
    this.confirmationService.confirm({
      message: 'Deseja resetar a senha da usuária?',
      header: 'Atenção',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          this.passwordResult = await lastValueFrom(
            this.personService
              .generateNewPassword({
                personId: this.person?.personId as number
              })
              .pipe(map((data) => data.result))
          );
        } catch (error: any) {
          AppDialogService.showErrorDialog(
            error,
            false,
            'Erro ao resetar a senha'
          );
        }
        LoaderService.showLoader(false);
      }
    });
  }

  fraudCancellation(): void {
    this.confirmationService.confirm({
      message:
        'As assinaturas serão canceladas, o CPF adicionado a Blacklist e o usuário será deslogado de todas as plataformas! \nObs Lembre-se de estornar os pagamentos no Gateway',
      header: 'Atenção, deseja apontar este usuário como fraude?',
      icon: 'pi pi-exclamation-triangle danger',
      acceptLabel: 'Concordo é uma Fraude!',
      rejectLabel: 'Voltar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        LoaderService.showLoader();
        try {
          const message = await lastValueFrom(
            this.subscriberService
              .cancelFraudAccount(this.person.personId)
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            detail: message,
            summary: 'Sucesso',
            severity: 'success'
          });
          LoaderService.showLoader(false);
          location.reload();
        } catch (error: any) {
          AppDialogService.showErrorDialog(
            error,
            false,
            'Erro ao Apontar como fraude'
          );
          LoaderService.showLoader(false);
        }
      }
    });
  }

  fraudReactivation(): void {
    const email = this.person?.username ?? 'usuário';
    const cpf = this.person?.cpf
      ? FormUtil.cpfFormatted(Number(this.person.cpf))
      : 'CPF não informado';

    this.confirmationService.confirm({
      message: `Deseja permitir o login do usuário ${email} e remover o bloqueio para pagamentos com o CPF: ${cpf}?`,
      header: 'Remover bloqueio por fraude',
      icon: 'pi pi-unlock',
      acceptLabel: 'Desbloquear conta',
      rejectLabel: 'Voltar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        LoaderService.showLoader();
        try {
          const message = await lastValueFrom(
            this.subscriberService
              .reactivateFraudAccount(this.person?.personId as number)
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            detail: message,
            summary: 'Sucesso',
            severity: 'success'
          });
          LoaderService.showLoader(false);
          location.reload();
        } catch (error: any) {
          AppDialogService.showErrorDialog(
            error,
            false,
            'Erro ao reativar conta'
          );
          LoaderService.showLoader(false);
        }
      }
    });
  }

  openPersonPage(): void {
    const url = '/users/person/' + this.person?.personId;
    // Se estiver no browser
    if (!PWAUtil.isPWA)
      window.open(
        this.router.serializeUrl(this.router.createUrlTree([url])),
        '_blank'
      );
    else this.router.navigate([url]);
  }

  hideMessage(): void {
    delete this.passwordResult;
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Copiado para área de trasnferência'
    });
  }

  get birthdayAsDate(): Date {
    return FormUtil.dateStringAsDate(this.person?.birthday as any);
  }

  get badgeStyle() {
    return {
      color: this.person?.hexColorCode || '#000000'
    };
  }

  get cpfFormatted() {
    return FormUtil.cpfFormatted(Number(this.person?.cpf));
  }

  get mensMarket() {
    return this.subscriptionId === 3 || this.subscriptionId === 4;
  }

  get phoneFormatted(): string {
    return !this.person?.phone?.includes('(')
      ? FormUtil.phoneFormatted(Number(this.person?.phone))
      : this.person?.phone || '';
  }

  get premium() {
    return SubscriptionId.isPremium(this.subscriptionId);
  }

  get imageClass() {
    switch (this.subscriptionId) {
      case SubscriptionEnum.Glambag:
      case SubscriptionEnum['Glambag Premium']:
        return 'image--bag';
      case SubscriptionEnum.Select:
        return 'image--select';
      case SubscriptionEnum.Prime:
        return 'image--prime';
      case SubscriptionEnum.Glamcombo:
      case SubscriptionEnum['Glamcombo Premium']:
        return 'image--combo';
      case SubscriptionEnum.Glampass:
        return 'image--pass';
      default:
        if (!this.subscriptionId && this.person?.businessUnitId === 2)
          return 'image--select';
        return '';
    }
  }
}
